/**
 * Who asked for which release file, from where, and whether they got it.
 *
 * This panel is the reason the whole feature records refusals rather than only
 * successes: a list of who downloaded disk1 is inventory, while a list of who
 * was REFUSED disk1 is the only thing anyone ever wants after an incident. So
 * refusals are rendered in the same table, marked, and reachable in one click
 * via the "Refused only" filter.
 *
 * IT LIVES INSIDE AN OPENED VERSION (`embedded`, with `releaseId`), beside the
 * buttons that produce its rows — it used to be a Download report TAB of its
 * own, which meant the answer to "did that customer ever get Disk 2 of
 * 10.2.0.27?" was two navigations and a filter away from the version it was
 * asked about. Embedded it shows this version only, at ten rows a page, with
 * the Release column dropped (every row says the same thing) and only the
 * filter worth having there.
 *
 * Rows come straight from the audit table's own copied columns, so an entry
 * keeps naming its release, artifact and person after any of the three is
 * deleted. Nothing here joins.
 */
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { JSX } from "react";

import { PageLoader } from "@/components/ui/page-loader";
import { Pagination } from "../../../components/ui/pagination.js";
import { ApiError } from "../../../lib/api.js";
import {
  ARTIFACT_KINDS,
  ARTIFACT_LABELS,
  DENIAL_REASONS,
  type DownloadLogFilters,
  listDownloads,
} from "./artifacts-api.js";

const headSx = {
  fontSize: 12,
  fontWeight: 500,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "text.disabled",
} as const;

const ACTOR_LABELS: Record<string, string> = {
  staff: "Staff",
  customer: "Customer",
  reseller: "Reseller",
  unknown: "Unauthenticated",
};

interface Props {
  /** Narrow to one release — every embedded use does. */
  releaseId?: number;
  /** The version those rows are about, for the empty state's wording. */
  version?: string;
  /** Render bare (no Card, no header, ten rows a page) for a version card. */
  embedded?: boolean;
}

export function ReleaseDownloadsPanel({ releaseId, version, embedded = false }: Props = {}): JSX.Element {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(embedded ? 10 : 25);
  const [outcome, setOutcome] = useState<DownloadLogFilters["outcome"]>("");
  const [kind, setKind] = useState("");
  const [actorKind, setActorKind] = useState("");
  const [email, setEmail] = useState("");

  const filters: DownloadLogFilters = {
    page,
    per_page: perPage,
    ...(releaseId ? { release_id: releaseId } : {}),
    ...(outcome ? { outcome } : {}),
    ...(kind ? { kind } : {}),
    ...(actorKind ? { actor_kind: actorKind } : {}),
    ...(email.trim() ? { email: email.trim() } : {}),
  };

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["admin", "release-downloads", filters],
    queryFn: () => listDownloads(filters),
  });

  // Any filter change resets to page 1 — otherwise a narrowed result set
  // leaves the pager pointing past its own end and the table reads empty.
  const onFilter = (apply: () => void): void => {
    apply();
    setPage(1);
  };

  const counts = data && (
    <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
      <Chip size="small" color="success" variant="outlined" label={`${data.grantedCount} granted`} />
      <Chip size="small" color="warning" variant="outlined" label={`${data.deniedCount} refused`} />
    </Box>
  );

  const filterRow = (
    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
      <TextField
        select size="small" label="Outcome" value={outcome ?? ""}
        onChange={(e) => onFilter(() => setOutcome(e.target.value as DownloadLogFilters["outcome"]))}
        sx={{ minWidth: 150 }}
      >
        <MenuItem value="">All</MenuItem>
        <MenuItem value="granted">Granted only</MenuItem>
        <MenuItem value="denied">Refused only</MenuItem>
      </TextField>
      {!embedded && (
        <>
          <TextField
            select size="small" label="File" value={kind}
            onChange={(e) => onFilter(() => setKind(e.target.value))}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">All files</MenuItem>
            {ARTIFACT_KINDS.map((k) => (
              <MenuItem key={k} value={k}>{ARTIFACT_LABELS[k]}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" label="Who" value={actorKind}
            onChange={(e) => onFilter(() => setActorKind(e.target.value))}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">Anyone</MenuItem>
            {Object.entries(ACTOR_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small" label="Email contains" value={email}
            onChange={(e) => onFilter(() => setEmail(e.target.value))}
            sx={{ minWidth: 200 }}
          />
        </>
      )}
      {embedded && counts}
    </Box>
  );

  const body = (
    <>
      {filterRow}
      {isLoading ? (
        <PageLoader />
      ) : isError ? (
        <Box sx={{ borderRadius: 1, border: 1, borderColor: "error.main", p: 2, fontSize: 14 }}>
          <Typography sx={{ mb: 1, color: "error.main" }}>
            Could not load the download log
            {error instanceof ApiError && error.message ? `: ${error.message}` : "."}
          </Typography>
          <Button variant="contained" size="small" onClick={() => void refetch()}>Retry</Button>
        </Box>
      ) : (data?.rows.length ?? 0) === 0 ? (
        <Typography sx={{ py: embedded ? 1.5 : 3, textAlign: "center", fontSize: embedded ? 13 : 14, color: "text.disabled" }}>
          {version ? `Nobody has asked for a file of ${version} yet.` : "No download attempts recorded yet."}
        </Typography>
      ) : (
        <>
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ "& td, & th": { fontSize: embedded ? 13 : 14 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headSx}>When</TableCell>
                  <TableCell sx={headSx}>Who</TableCell>
                  {!embedded && <TableCell sx={headSx}>Release</TableCell>}
                  <TableCell sx={headSx}>File</TableCell>
                  <TableCell sx={headSx}>Outcome</TableCell>
                  <TableCell sx={headSx}>From</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {new Date(row.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "grid" }}>
                        <span>{row.user_email ?? "—"}</span>
                        <Typography component="span" sx={{ fontSize: 11, color: "text.disabled" }}>
                          {ACTOR_LABELS[row.actor_kind] ?? row.actor_kind}
                          {row.reseller_name ? ` · ${row.reseller_name}` : ""}
                        </Typography>
                      </Box>
                    </TableCell>
                    {!embedded && <TableCell>{row.release_version ?? "—"}</TableCell>}
                    <TableCell>{row.artifact_label}</TableCell>
                    <TableCell>
                      {row.outcome === "granted" ? (
                        <Chip size="small" color="success" variant="outlined" label="Granted" />
                      ) : (
                        <Tooltip title={DENIAL_REASONS[row.reason] ?? row.reason}>
                          <Chip size="small" color="warning" variant="outlined" label="Refused" />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>{row.ip_address ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 1.5 }}>
            <Pagination
              page={page}
              perPage={perPage}
              totalCount={data?.totalCount ?? 0}
              totalPages={data?.totalPages ?? 1}
              onPageChange={setPage}
              onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
              entityNoun="attempts"
              hideIfSinglePage={embedded}
            />
          </Box>
        </>
      )}
    </>
  );

  if (embedded) return <Box data-testid={`release-${version ?? releaseId}-downloads`}>{body}</Box>;

  return (
    <Card>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          borderBottom: 1,
          borderColor: "divider",
          px: 2.5,
          py: 2,
        }}
      >
        <Box sx={{ display: "grid", gap: 0.25 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}>
            Download log
          </Typography>
          <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
            Every attempt on a release’s notes or disk images — customers, resellers and staff
            alike. Refusals are recorded too.
          </Typography>
        </Box>
        {counts}
      </Box>

      <CardContent sx={{ px: 2.5, py: 2 }}>{body}</CardContent>
    </Card>
  );
}
